import { useCallback, useEffect, useState } from "react";

import { useAccountData } from "../firebase";
import { fetchOurApi } from "../utils/apiCalls";

export const useCustomerCoupon = (purchaseType) => {
  const [{ stripeCustomerId }, accountDataLoading] = useAccountData();

  const [coupon, setCoupon] = useState();
  const [isCouponLoading, setIsCouponLoading] = useState(true);

  useEffect(() => {
    const handleSetCoupon = (coupon) => {
      const { purchaseType: couponPurchaseType } = coupon;

      if (!purchaseType) {
        return setCoupon(coupon);
      }

      if (couponPurchaseType === purchaseType) {
        return setCoupon(coupon);
      }

      return null;
    };

    if (!accountDataLoading && isCouponLoading && !coupon) {
      fetchOurApi({
        path: "/stripe/get-customer-coupon",
        method: "POST",
        data: {
          customerId: stripeCustomerId,
        },
        callback: (coupon) => {
          if (!!coupon || !Object.keys(coupon).length) {
            handleSetCoupon(coupon);
          }

          return setIsCouponLoading(false);
        },
      });
    }
  }, [
    accountDataLoading,
    coupon,
    isCouponLoading,
    stripeCustomerId,
    purchaseType,
  ]);

  return [coupon, isCouponLoading];
};

export const useCustomerInvoices = () => {
  const [{ stripeCustomerId }, accountDataLoading] = useAccountData();

  const [invoices, setInvoices] = useState([]);
  const [isInvoicesLoading, setIsInvoicesLoading] = useState(true);

  useEffect(() => {
    if (!accountDataLoading && isInvoicesLoading && !invoices.length) {
      fetchOurApi({
        path: "/stripe/get-customer-invoices",
        method: "POST",
        data: {
          customerId: stripeCustomerId,
        },
        callback: (invoices) => {
          if (!!invoices && invoices.length) {
            const formattedDateInvoices = invoices.map(
              ({ date, ...otherFields }) => ({
                date: new Date(date),
                ...otherFields,
              })
            );

            setInvoices(formattedDateInvoices);
          }

          return setIsInvoicesLoading(false);
        },
      });
    }
  }, [accountDataLoading, invoices, isInvoicesLoading, stripeCustomerId]);

  return [invoices, isInvoicesLoading];
};

export const useCustomerPaymentMethod = (givenStripeCustomerId) => {
  const [{ stripeCustomerId }, accountDataLoading] = useAccountData();

  const [paymentMethod, setPaymentMethod] = useState({});
  const [isPaymentMethodLoading, setIsPaymentMethodLoading] = useState(true);

  const handleSetPaymentMethod = (paymentMethodObj) => {
    const { card } = paymentMethodObj;

    if (card) {
      const { expirationDate } = card;

      const cardPaymentMethodObj = {
        ...paymentMethodObj,
        card: {
          ...card,
          expirationDate: new Date(expirationDate),
        },
      };

      return setPaymentMethod(cardPaymentMethodObj);
    }

    return setPaymentMethod(paymentMethodObj);
  };

  const refetchCustomerPaymentMethod = useCallback(async () => {
    if (!stripeCustomerId) {
      return setIsPaymentMethodLoading(false);
    }

    return await fetchOurApi({
      path: "/stripe/get-customer-payment-method",
      method: "POST",
      data: {
        customerId: givenStripeCustomerId || stripeCustomerId,
      },
      callback: (paymentMethod) => {
        if (!!paymentMethod || !Object.keys(paymentMethod).length) {
          handleSetPaymentMethod(paymentMethod);
        }

        return setIsPaymentMethodLoading(false);
      },
    });
  }, [stripeCustomerId, givenStripeCustomerId]);

  useEffect(() => {
    if (
      !accountDataLoading &&
      isPaymentMethodLoading &&
      !Object.keys(paymentMethod).length
    ) {
      refetchCustomerPaymentMethod();
    }
  }, [
    accountDataLoading,
    isPaymentMethodLoading,
    paymentMethod,
    refetchCustomerPaymentMethod,
  ]);

  return {
    paymentMethod,
    isPaymentMethodLoading,
    refetchCustomerPaymentMethod,
  };
};
